import { BackIcon, EqualIcon, GreaterThanOrEqualIcon, LessThanOrEqualIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { RadioIcon, Select, TextInput } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { FloatComparator } from '@generated/graphql';
import { Divider, MenuItem, RadioGroup, Stack } from '@mui/material';
import { useState } from 'react';
import { FilterComponent } from './types';

export const SizeFilter: FilterComponent<FloatComparator[]> = ({
  title,
  filter,
  onApply,
  onBack,
}) => {
  if (filter.type !== 'size') {
    throw new Error('SizeFilter does not support this filter type');
  }
  const value = filter.value?.[0];
  const display = filter.display?.[0] as 'kB' | 'MB' | 'GB';

  const [dirtyValue, setDirtyValue] = useState(() => {
    const val = value ? Object.values(value)?.[0] : null;
    if (val) {
      return display === 'kB' ? val / 1000 : display === 'MB' ? val / 1000000 : val / 1000000000;
    }
    return '';
  });
  const [dirtyOption, setDirtyOption] = useState(() => (value ? Object.keys(value)?.[0] : 'eq'));
  const [dirtyDisplay, setDirtyDisplay] = useState<'kB' | 'MB' | 'GB'>(display || 'MB');
  return (
    <Stack maxHeight="500px">
      <Group alignItems="center" gap={1} sx={{ px: 2 }}>
        <IconButton size={16} onClick={onBack} Icon={BackIcon} />
        <Typography variant="body2">{title}</Typography>
      </Group>
      <Divider sx={{ m: 2 }} flexItem />
      <Stack sx={{ overflowY: 'auto', px: 6, gap: 2 }}>
        <RadioGroup
          row
          sx={{
            flex: 1,
            gap: 1,
            justifyContent: 'center',
            '& .MuiFormControlLabel-label': { height: '24px', lineHeight: '24px' },
          }}
          value={dirtyOption}
          onChange={(e) => setDirtyOption(e.target.value)}
        >
          <RadioIcon value="lte" icon={LessThanOrEqualIcon} />
          <RadioIcon value="eq" icon={EqualIcon} />
          <RadioIcon value="gte" icon={GreaterThanOrEqualIcon} />
        </RadioGroup>
        <Group sx={{ gap: 2 }}>
          <TextInput
            value={dirtyValue}
            onChange={(e) => setDirtyValue(Number(e.target.value))}
            type="number"
            fullWidth
            containerProps={{ sx: { flex: 1, width: '100%' } }}
          />
          <Select
            value={dirtyDisplay}
            onChange={(e) => setDirtyDisplay(e.target.value as 'kB' | 'MB' | 'GB')}
            containerProps={{ sx: { width: '75px' } }}
          >
            <MenuItem value="kB">kB</MenuItem>
            <MenuItem value="MB">MB</MenuItem>
            <MenuItem value="GB">GB</MenuItem>
          </Select>
        </Group>
      </Stack>
      <Divider sx={{ m: 2 }} flexItem />
      <Group justifyContent="flex-end" sx={{ px: 2, gap: 2 }}>
        <Button
          size="small"
          variant="secondary"
          onClick={() => {
            setDirtyValue('');
          }}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="primary"
          onClick={() => {
            if (!dirtyValue) {
              onBack();
            }
            onApply({
              value: [
                {
                  [dirtyOption]:
                    Number(dirtyValue) *
                    (dirtyDisplay === 'kB' ? 1000 : dirtyDisplay === 'MB' ? 1000000 : 1000000000),
                },
              ],
              display: [dirtyDisplay],
            });
          }}
        >
          Apply
        </Button>
      </Group>
    </Stack>
  );
};
